<script lang="ts" setup>
import ButtonBase from '#components/elements/button/ButtonBase.vue';
import type { TreeItemsStateType, TreeViewItemType } from '#components/treeView';
import TreeView from '#components/treeView/TreeView.vue';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue';
import { computed, type PropType } from 'vue';
import type { TreeView as LocalTreeView } from './composables/useProductData';

const props = defineProps({
  items: {
    type: Object as PropType<TreeViewItemType | LocalTreeView | null>,
    required: false,
    default: null
  },
  defaultOpen: {
    type: Boolean,
    required: true
  },
  title: {
    type: String,
    required: true
  },
  placeholder: {
    type: String,
    required: false,
    default: undefined
  },
  testId: {
    type: String,
    required: false,
    default: undefined
  },
  isLoading: {
    type: Boolean,
    required: false,
    default: false
  },
  loadingItemsCount: {
    type: Number,
    required: false,
    default: 6
  },
  loadingItemsHasArrow: {
    type: Boolean,
    required: false,
    default: false
  },
  useVirtualScrolling: {
    type: Boolean,
    required: false,
    default: true
  }
});

const emit = defineEmits<{
  onCheckedItemsChange: [state: TreeItemsStateType];
}>();

const isItems = computed(() => props.items && Array.isArray(props.items.children) && props.items.children.length > 0);
</script>
<template>
  <Disclosure
    v-if="placeholder || isItems || isLoading"
    v-slot="{ open }"
    as="div"
    :default-open="!!placeholder || defaultOpen"
  >
    <DisclosureButton as="template">
      <ButtonBase
        variant="tertiary"
        size="medium"
        :icon-left="open ? 'chevron-up' : 'chevron-down'"
        :test-id="`disclosure-${testId}`"
      >
        <span class="opacity-50">{{ title }}</span>
      </ButtonBase>
    </DisclosureButton>

    <DisclosurePanel as="div">
      <div v-if="placeholder && !isItems && !isLoading" class="text-sm text-gray-700 px-8">
        <i>({{ placeholder }})</i>
      </div>

      <TreeView
        :items="(items as TreeViewItemType)?.children ?? []"
        :use-virtual-scroll="useVirtualScrolling"
        :is-loading="isLoading"
        :loading-items-count="loadingItemsCount"
        :loading-items-has-arrow="loadingItemsHasArrow"
        @on-checked-items-change="(checkedItems) => emit('onCheckedItemsChange', checkedItems)"
      />
    </DisclosurePanel>
  </Disclosure>
</template>
